import { EventEmitter, Injectable, Injector } from "@angular/core";

import { Observable } from "rxjs";
import { FormStatusEnum, FormType, Tag, ResourceType } from "src/app/common/enumerations/enumerations";
import { BaseService } from "src/app/common/base/base.service";
import { Kind, RSF, RSFAmendment, RSFHoldPoint, RSFRelatedDocumentType, RSFResourceRelationType, RSFStatusResource } from "./rsf";
import { WFTaskSignatureResource, SaveTaskResource, WfSignature, WFAction, WfSignatureResource } from "../workflow/workflow";
import { IFormService } from "src/app/common/interfaces/i-form-service";
import { ArrayCompareFilter, compareByKind, compositeKey, removeCircularReferences } from "src/app/modules/libs/utils";
import { TextareaDialogComponent } from "src/app/controls/textarea-dialog/textarea-dialog.component";
import { User } from "../catalogs/user-catalog/services/user";
import { KEService } from "../kenable/ke.service";

export enum RSFConfiguration {
  ApprovalsSectionNumber = 2,
  AmendmentApprovalsSectionNumber = 8
}

@Injectable({
  providedIn: "root",
})
export class RsfService extends BaseService<RSF, number> implements IFormService {

  CheckSelectedSection1: EventEmitter<any> = new EventEmitter<any>();
  SerialNumber = "/SerialNumbers/" + FormType.RSF;
  Api = "/RSF";
  ApiAmendment = "/RSFAmendment";
  rsf?: RSF | null;

  constructor(
    protected override injector: Injector,
    private keService: KEService,
  ) {
    super(injector, '/RSF');
  }

  /**
   * Confirm and submit a signature action for a workflow task.
   *
   * This method performs validation, optionally prompts the user for confirmation,
   * determines the resulting form status based on the chosen action and task code,
   * constructs a WFTaskSignatureResource payload, and submits it via the service's
   * sign() method. Any errors returned from the sign() call are caught, a default
   * error alert is shown, and the error is logged to the console.
   *
   * Behavior summary:
   * - Returns false immediately if any of the required inputs (form, task, actionId)
   *   are missing.
   * - Stores the provided form on this.rsf.
   * - Extracts an optional reason from data.text.
   * - Finds the action by actionId on task.wfTaskLocal.wfActionLocals.
   * - If the action is an approval and a confirmation message exists, shows a dialog
   *   and:
   *     - If the user cancels, returns false.
   *     - If confirmed, uses the status from the confirmation message.
   * - If task.code === Tag.NO_WORK_CHECK:
   *     - Sets status to FormStatusEnum.PendingCancelation for approve actions,
   *       otherwise to FormStatusEnum.Active.
   * - Otherwise: if the action is Close, sets status to FormStatusEnum.Closed.
   * - Builds a WFTaskSignatureResource with taskId, actionId, reason, formID, options,
   *   and the computed status, then calls sign(...).toPromise().
   * - Catches sign() errors, calls this.alert.defaultError(), logs the error, and
   *   still resolves to true.
   *
   * @param form - The form object to sign (may be undefined; method returns false if missing).
   * @param task - The workflow signature/task context (required).
   * @param actionId - The identifier of the action to perform on the task (required).
   * @param data - Optional payload containing additional data; this method reads data.text as the reason.
   * @param options - Optional string of additional options to include in the signature payload.
   *
   * @returns A Promise that resolves to:
   *  - false when required inputs are missing or when the user cancels a confirmation dialog;
   *  - true after attempting to submit the signature (even if the submission fails, errors are handled internally).
   *
   * @remarks
   * - Side effects: updates this.rsf, may show a confirmation dialog (this.showDialog),
   *   submits to this.sign(...).toPromise(), and may call this.alert.defaultError().
   * - Relies on the enums/types: WFAction, FormStatusEnum, Tag, WFTaskSignatureResource.
   */
  async confirmSign(form: any | undefined, task: WfSignature, actionId?: number, data?: any, options?: string) {
    if (!form || !task || !actionId) return false;

    this.rsf = form;
    let reason = data?.text;

    const action = task.wfTaskLocal?.wfActionLocals?.find(a => a.id == actionId);
    const confirmMessage = action?.action == WFAction.Approve ? this.getConfirmMessage(task) : null;

    let status = 0;
    if (confirmMessage) {
      const confirmed = await this.showDialog(confirmMessage);
      if (!confirmed) return false;
      else status = confirmMessage.status;
    }

    if (task.code === Tag.NO_WORK_CHECK) {
      if (action?.action == WFAction.Approve) {
        const cancelData = await this.computeCancelReason();
        if (cancelData.acceptClicked) {
          status = FormStatusEnum.PendingCancelation;
          reason = cancelData.text;
        }
      }
      else {
        status = FormStatusEnum.Active;
      }
    }
    else {
      status = action?.action == WFAction.Close ? FormStatusEnum.Closed : status;
    }

    const taskSignature = {
      taskId: task.taskId,
      actionId,
      reason,
      formID: this.rsf?.id,
      options,
      status,
    } as WFTaskSignatureResource;
    const signedRSF = await this.sign(taskSignature).toPromise()
      .catch(error => {
        this.alert.defaultError();
        console.error(error);
      });

    // Refresh open KE checklists when the RSF becomes active or a new amendment becomes active.
    const previousActiveAmendment = this.rsf?.rsfAmendments?.find(amendment => amendment.active);
    const signedActiveAmendment = signedRSF?.rsfAmendments?.find(amendment => amendment.active);
    const rsfBecameActive = this.rsf?.status != FormStatusEnum.Active && signedRSF?.status == FormStatusEnum.Active;
    const amendmentBecameActive = signedActiveAmendment?.id != null && signedActiveAmendment.id != previousActiveAmendment?.id;
    if (signedRSF?.status == FormStatusEnum.Active && (rsfBecameActive || amendmentBecameActive)) {
      const activeRSF = signedRSF.rsfAmendments?.find(a => a.active) ?? signedRSF;
      const restrictedResourceIDs = activeRSF.rsfRestrictions
        ?.map(restriction => restriction.restriction?.resourceID)
        .filter((resourceID): resourceID is number => resourceID !== undefined);
      if (restrictedResourceIDs?.length) {
        this.keService.processKES(restrictedResourceIDs);
      }
    }
    return true;
  }


  /**
   * Computes an optional confirmation message for a workflow signature action.
   *
   * Evaluates the given task in the context of the current RSF state (section/step/tasks)
   * and determines whether the UI should present a confirmation dialog before proceeding.
   *
   * Behavior by task.code:
   * - Tag.ACTIVE_FORM
   *   - If the current RSF is of kind Kind.RSF and the associated step contains exactly one
   *     enabled, unsigned workflow task, returns a warning message (key: "toActiveWarning"),
   *     icon "warn" and status 0.
   * - Tag.REQ
   *   - If there exists any active RSF amendment and safety controls have not changed,
   *     returns a warning message (key: "toActiveWarning"), icon "warn" and status 0.
   * - Tag.NO_WORK_OIC, Tag.NO_WORK_RWA
   *   - If the associated step contains exactly one enabled, unsigned workflow task,
   *     returns a warning message (key: "CancelDialogWarning"), icon "warn" and
   *     status FormStatusEnum.Canceled.
   * - default
   *   - No confirmation needed; returns null.
   *
   * The method navigates the RSF structure via this.rsf?.wfTable?.wfTableLocal to find the
   * relevant section and step, and inspects step.wfTaskLocals -> wfTasks. Optional chaining
   * is used so missing context results in a null (no confirmation) outcome.
   *
   * @private
   * @param task - The WfSignature task being evaluated for a potential confirmation.
   * @returns An object with { message, icon, status } when a confirmation should be shown,
   *          or null when no confirmation is required.
   */
  private getConfirmMessage(task: WfSignature): { message: string, icon: string, status: FormStatusEnum; } | null {
    const sectionLocal = this.rsf?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.id == task.wfTaskLocal?.wfStepLocal?.wfSectionLocalID);
    const stepLocal = sectionLocal?.wfStepLocals?.find(s => s.id == task.wfTaskLocal?.wfStepLocalID);
    const wfTasks = stepLocal?.wfTaskLocals?.flatMap(t => t.wfTasks);
    switch (task.code) {

      case Tag.ACTIVE_FORM:
        if (this.rsf?.kind == Kind.RSF && wfTasks?.filter(t => t?.signedBy == null && t?.enabled).length == 1) {
          return { message: this.getMessage('toActiveWarning').description, icon: "warn", status: 0 };
        }
        else return null;

      case Tag.REQ:
        if (this.rsf?.rsfAmendments.some(a => a.active) && !this.rsf.safetyControlsChanged) {
          return { message: this.getMessage('toActiveWarning').description, icon: "warn", status: 0 };
        }
        else return null;

      case Tag.NO_WORK_OIC:
      case Tag.NO_WORK_RWA:
        if (wfTasks?.filter(t => t?.signedBy == null && t?.enabled).length == 1)
          return { message: this.getMessage('CancelDialogWarning').description, icon: "warn", status: FormStatusEnum.Canceled };
        else return null;

      default:
        return null;
    };
  }

  async computeCancelReason(): Promise<any> {
    const confirm = this.dialog.open(TextareaDialogComponent, {
      width: '400px',
      data: {
        label: this.getMessage('disapprove_message')?.description,
        placeholder: 'Disapproval Reason',
        text: ''
      }
    });
    return new Promise(resolve => {
      confirm.afterClosed().subscribe((data) => {
        resolve(data);
      });
    });
  }

  // Gets the Serial Number of the Form
  getSerialNo(): Observable<SerialNumber> {
    const httpresult1 = this.http.get<SerialNumber>(
      `${this.BASE_URL}${this.SerialNumber}`
    );
    return httpresult1;
  }

  updateAmendment(rsfAmendment: RSFAmendment): Observable<RSFAmendment> {
    return this.http.put<RSFAmendment>(`${this.BASE_URL}${this.ApiAmendment}`, rsfAmendment);
  }

  updateMinorAmendment(rsfAmendment: RSFAmendment): Observable<RSFAmendment> {
    return this.http.put<RSFAmendment>(`${this.BASE_URL}${this.ApiAmendment}/UpdateMinor`, rsfAmendment);
  }

  cancel(rsf: RSF, reason: string) {
    return this.http.patch(`${this.BASE_URL}${this.Api}/Cancel`, {
      id: rsf.id,
      reason,
    });
  }

  sign(taskSignature: WFTaskSignatureResource) {
    const httpresult = this.http.put<RSF>(`${this.BASE_URL}${this.Api}/Sign`, taskSignature);
    return httpresult;
  }

  /**
   * Non-production tester tooling: advances an RSF to a target status by applying every required
   * signature in workflow order, server-side, in a single request (one reload instead of N per
   * signature). The endpoint is non-prod gated and additionally requires a System Administrator
   * caller. See RSFController.TestAdvance. `targetStatus` is a FormStatusEnum value.
   */
  testAdvance(rsfId: number, targetStatus: number): Observable<RSF> {
    return this.http.put<RSF>(`${this.BASE_URL}${this.Api}/TestAdvance/${rsfId}/${targetStatus}`, null);
  }

  /**
   * Non-production tester tooling: signs the task on behalf of the appropriate role-holder,
   * mirroring {@link confirmSign} but WITHOUT the confirmation/cancel-reason popups (the tester
   * button's tooltip is deemed sufficient) and hitting the server-side TestSign endpoint. The same
   * target status is computed so the backend drives the same transition (and thus the same
   * olog/email) a real signature would. The server-side non-prod + SA guards are the real gate.
   */
  async confirmTestSign(form: any | undefined, task: WfSignature, actionId?: number, data?: any, options?: string) {
    if (!form || !task || !actionId) return false;

    this.rsf = form;
    let reason = data?.text;

    const action = task.wfTaskLocal?.wfActionLocals?.find(a => a.id == actionId);
    // Same status the real flow computes, but without showing the dialog.
    const confirmMessage = action?.action == WFAction.Approve ? this.getConfirmMessage(task) : null;
    let status = confirmMessage?.status ?? 0;

    if (task.code === Tag.NO_WORK_CHECK) {
      if (action?.action == WFAction.Approve) {
        // Real flow prompts for a cancel reason; tester tooling proceeds with a default one.
        status = FormStatusEnum.PendingCancelation;
        reason = reason ?? 'Auto-signed by tester tools (no work performed).';
      }
      else {
        status = FormStatusEnum.Active;
      }
    }
    else {
      status = action?.action == WFAction.Close ? FormStatusEnum.Closed : status;
    }

    const taskSignature = {
      taskId: task.taskId,
      actionId,
      reason,
      formID: this.rsf?.id,
      options,
      status,
    } as WFTaskSignatureResource;
    await this.testSign(taskSignature).toPromise()
      .catch(error => {
        this.alert.defaultError();
        console.error(error);
      });
    return true;
  }

  testSign(taskSignature: WFTaskSignatureResource) {
    const httpresult = this.http.put<RSF>(`${this.BASE_URL}${this.Api}/TestSign`, taskSignature);
    return httpresult;
  }

  saveTask(s: SaveTaskResource, refresh = true) {
    const httpresult = this.http.put<any>(`${this.BASE_URL}${this.Api}/SaveTask/${s.id}/${refresh}`, s);
    return httpresult;
  }

  saveTasks(saveTasks: SaveTaskResource[], refresh = true) {
    const httpresult = this.http.put<any>(`${this.BASE_URL}${this.Api}/SaveTasks/${refresh}`, saveTasks);
    return httpresult;
  }

  duplicate(id: number, isAmendment: boolean): Observable<RSF> {
    return this.http.post<RSF>(`${this.BASE_URL}${this.Api}/duplicate/${id}/${isAmendment}`, null);
  }

  putStatus(rsf: RSFStatusResource) {
    return this.http.put<RSF>(`${this.BASE_URL}${this.Api}/SetStatus/${rsf.id}`, rsf);
  }

  save(form: RSF): Promise<RSF | undefined> {
    const rsf = removeCircularReferences(form);
    return this.update(rsf).toPromise();
  }

  setEditing(id: number, value?: boolean, amendmentID?: number): Observable<number> {
    const httpresult2 = this.http.put<number>(`${this.BASE_URL}${this.Api}/SetEditing/${id}`, { Value: value, AmendmentID: amendmentID });
    return httpresult2;
  }

  // Amendments
  requestAmendment(id: number): Observable<any> {
    return this.http.put<any>(`${this.BASE_URL}${this.Api}/RequestAmendment/${id}`, null);
  }

  requestMinorAmendment(id: number): Observable<any> {
    return this.http.put<any>(`${this.BASE_URL}${this.Api}/RequestMinorAmendment/${id}`, null);
  }

  cancelAmendment(rsf: RSF, reason: string) {
    return this.http.patch(`${this.BASE_URL}${this.ApiAmendment}/Cancel`, {
      id: rsf.id,
      reason,
    });
  }


  finishAmendment(id: number): Observable<any> {
    return this.http.put<any>(`${this.BASE_URL}${this.Api}/FinishAmendment/${id}`, null);
  }

  getScienstists(rsfId: number): Observable<User[]> {
    return this.http.get<User[]>(`${this.BASE_URL}${this.Api}/GetBlScientists/${rsfId}`);
  }


  // Hold Points
  createHoldPoint(holdPoint: RSFHoldPoint): Observable<RSFHoldPoint> {
    return this.http.post<RSFHoldPoint>(`${this.BASE_URL}${this.Api}/CreateHoldPoint`, holdPoint);
  }

  updateHoldPoint(holdPoint: RSFHoldPoint): Observable<RSFHoldPoint> {
    return this.http.put<RSFHoldPoint>(`${this.BASE_URL}${this.Api}/UpdateHoldPoint`, holdPoint);
  }

  updateHoldPointNumbers(holdPoints: RSFHoldPoint[]): Observable<RSFHoldPoint> {
    return this.http.put<RSFHoldPoint>(`${this.BASE_URL}${this.Api}/UpdateHoldPointNumbers`, holdPoints);
  }

  deleteHoldPoint(holdPoint: RSFHoldPoint) {
    return this.http.put(`${this.BASE_URL}${this.Api}/DeleteHoldPoint`, holdPoint);
  }

  setAmendmentRequesters(wfSignatures: WfSignatureResource[]): WfSignatureResource[] {
    const rsfAmendment = this.rsf?.rsfAmendments.find(a => a.active);
    const requester = rsfAmendment ? this.getUser(rsfAmendment.requesterID ?? 0) : this.getUser(this.rsf?.requesterID ?? 0);
    if (requester)
      wfSignatures?.filter(s => s.requesterSignature && !s.signed).forEach(s => {
        s!.users = [requester];
        s.hilite = requester.id != this.rsf?.requesterID;
        s.wfTaskLocal.wfUserLocals?.forEach(ul => {
          ul.userID = requester.id!;
          ul.user = requester;
        });
      });

    return wfSignatures;
  }

  checkDiff(
    field: Field,
    rsf: RSF,
    rsfAmendment: RSFAmendment,
  ): boolean {
    const filter = this.computeFilter(field);
    const key = filter.field as unknown as RSFKey;
    const left = rsf[key];
    const right = rsfAmendment[key];
    if (left === undefined && right === undefined) return false;

    return compareByKind(left, right, filter.filter);
  }

  computeFilter(field: Field): { field: Field, filter?: ArrayCompareFilter; } {
    switch (field) {
      case Field.ASCCForms:
        return {
          field: Field.RelatedDocuments,
          filter:
          {
            predicate: (d: any) => d.type === RSFRelatedDocumentType.ASCCForm,
            by: (d: any) => compositeKey(d.relatedDocumentID, d.relatedDocumentType),          // or 'id'
            select: ['relatedDocumentID', 'relatedDocumentType'],      // project to a stable subset
            orderInsensitive: false         // require same order too
          }
        };

      case Field.ParentForms:
        return {
          field: Field.RelatedDocuments,
          filter:
          {
            predicate: (d: any) => d.type === RSFRelatedDocumentType.ParentForm,
            by: (d: any) => compositeKey(d.relatedDocumentID, d.relatedDocumentType),          // or 'id'
            select: ['relatedDocumentID', 'relatedDocumentType'],      // project to a stable subset
            orderInsensitive: false         // require same order too
          }
        };

      case Field.ChildForms:
        return {
          field: Field.RelatedDocuments,
          filter:
          {
            predicate: (d: any) => d.type === RSFRelatedDocumentType.ChildForm,
            by: (d: any) => compositeKey(d.relatedDocumentID, d.relatedDocumentType),          // or 'id'
            select: ['relatedDocumentID', 'relatedDocumentType'],      // project to a stable subset
            orderInsensitive: false         // require same order too
          }
        };

      case Field.RelatedLinks:
        return {
          field: Field.RelatedLinks,
          filter:
          {
            by: (d: any) => compositeKey(d.url, d.text),
            select: ['url', 'text'],      // project to a stable subset
            orderInsensitive: false         // require same order too
          }
        };

      case Field.BeamlineLocations:
        return {
          field: Field.ResourceRelations,
          filter:
          {
            predicate: (d: any) => d.type === RSFResourceRelationType.Beamline,
            by: (d: any) => d.resourceID,
            select: ['resourceID'],      // project to a stable subset
            orderInsensitive: false         // require same order too
          }
        };

      case Field.AcceleratorLocations:
        return {
          field: Field.ResourceRelations,
          filter:
          {
            predicate: (d: any) => d.type === RSFResourceRelationType.Accelerator,
            by: (d: any) => d.resourceID,
            select: ['resourceID'],      // project to a stable subset
            orderInsensitive: false         // require same order too
          }
        };

      case Field.Restrictions:
        return {
          field: Field.Restrictions,
          filter:
          {
            // Restriction rows have no resourceID; keying on it mapped every row
            // to undefined so only count changes were detected.
            by: (d: any) => compositeKey(d.restrictionID, d.restrictionText),
            select: ['restrictionID', 'restrictionText'],
            orderInsensitive: true          // restrictions diff as a set, like the other forms
          }
        };

      default:
        return { field };
    }
  }

  parseNotificationConditions(rsf: RSF) {
    return this.http.patch<RSF>(this.URL + '/ParseNotificationConditions', rsf);
  }

}

class SerialNumber {
  serialNo!: string;
}

export class RSFPrivileges {
  canCreate!: boolean | null;
  canEdit!: boolean | null;
  canSubmit!: boolean | null;
  canUnsubmit!: boolean | null;
  canCancel!: boolean | null;
  canViewAmendment!: boolean | null;
  canCreateAmendment!: boolean | null;
  canCreateMinorAmendment!: boolean | null;
  canEditAmendment!: boolean | null;
  canSubmitAmendment!: boolean | null;
  canUnsubmitAmendment!: boolean | null;
  canCancelAmendment!: boolean | null;
  canDuplicate!: boolean | null;
  canRelease!: boolean | null;
  canAddHoldPoints!: boolean | null;
  canAddHoldPointsDraft!: boolean | null;
  canDisapprove?: boolean | null;
}

export class RSFStatus {
  isCreating!: boolean | null;
  isEditing!: boolean | null;
  isViewingAmendment!: boolean | null;
  isCreatingAmendment!: boolean | null;
  isEditingAmendment!: boolean | null;
  isMinorAmendment?: boolean | null;
  // Allows RSF section components to suppress live edit behavior when rendered in History Log.
  isLogView?: boolean | null;
}


/**
 * Enumeration of all possible fields used in the RSF Main S1 component.
 *
 * Each member represents a specific data field or section relevant to the RSF process,
 * such as links, documents, forms, locations, systems, and various configuration or
 * monitoring options.
 *
 * @remarks
 * This enum is used to reference and manage the different fields programmatically
 * throughout the component logic.
 */
// Enum values are RSF keys
export enum Field {
  ASCCForms = 'asccForms',
  ParentForms = 'parentForms',
  ChildForms = 'childForms',
  RelatedDocuments = 'rsfRelatedDocuments',
  RelatedLinks = 'rsfRelatedLinks',
  Locations = 'locations',
  WorkDescription = 'description',
  AffectedSystems = 'affectedSystems',
  Beamline = 'beamline',
  BeamlineLocations = 'beamlineLocations',
  Accelerator = 'accelerator',
  AcceleratorLocations = 'acceleratorLocations',
  OtherSystem = 'otherSystem',
  OtherSystems = 'otherSystems',
  ResourceRelations = 'rsfResourceRelations',
  Shielding = 'shielding',
  ShieldingRemoveReplace = 'shieldingRemoveReplace',
  ShieldingModification = 'shieldingModification',
  ShieldingModificationARC = 'shieldingModificationARC',
  ShieldingModificationBRC = 'shieldingModificationBRC',
  PPS = 'pps',
  PPSMaintenance = 'ppsMaintenance',
  PPSModification = 'ppsModification',
  PPSModificationARC = 'ppsModificationARC',
  PPSModificationBRC = 'ppsModificationBRC',
  PPSAcis = 'ppsAcis',
  TOMBIS = 'tombis',
  PPSOther = 'ppsOther',
  PPSOtherText = 'ppsOtherText',
  PPSPLCSet = 'ppsplcSet',
  PPSSoftware = 'ppsSoftware',
  ETIAccess = 'etiAccess',
  TOMBISPLCSet = 'tombisplcSet',
  TOMBISSoftware = 'tombisSoftware',
  TopOffInterlockSetPoint = 'topOffInterlockSetPoint',
  Requester = 'requesterID',
  InitDate = 'estimatedStartOn',
  EndDate = 'estimatedEndOn',
  RadMonitors = 'radMonitors',
  RadMonitorsMoving = 'radMonitorsMoving',
  RadMonitorsDisconnecting = 'radMonitorsDisconnecting',
  RadMonitorsModifying = 'radMonitorsModifying',
  RadMonitorsTripPoint = 'radMonitorsTripPoint',
  Restrictions = 'rsfRestrictions',
  FunctionalTest = 'functionalTest',
  FunctionalTestChips = 'functionalTestChips',
  RADSurveyRequired = 'radSurveyRequired'
}

/**
 * Formats resource display name with shutter information.
 * Combines beamline/resource name with associated shutter name if available.
 * Prevents duplicate display if shutter name is already in the resource name.
 * @param rr Resource relation object with name and nested resource data
 * @returns Formatted display name: "Beamline / Shutter" or just "Beamline" if shutter already included
 */
export function getResourceDisplayName(rr: any): string {
  // Use the resource's parentResources if available, otherwise fall back to the relation name
  let shutterName: string | undefined;
  const resourceName = rr.name || rr.resource?.name;

  // Try multiple ways to find shutter name:
  // 1. From resource parentResources
  if (rr.resource?.parentResources?.length) {
    shutterName = rr.resource.parentResources.find(
      (pr: any) => pr.parentResourceType === ResourceType.Shutter
    )?.parentResourceName;
  }

  if (rr.parentResources?.length) {
    shutterName = rr.parentResources.find(
      (pr: any) => pr.parentResourceType === ResourceType.Shutter
    )?.parentResourceName;
  }

  // 2. If not found, check if shutter info is in the resource itself
  if (!shutterName && resourceName) {
    // The resource might be a child of a shutter, and shutter name could be embedded
    const parentMatch = resourceName?.match(/^(.+)\s+\//);
    if (parentMatch) {
      shutterName = parentMatch[1];
    }
  }

  // 3. Check if rr object directly has parentResourceName (for cases where rr is the parent)
  if (!shutterName && rr.parentResourceName) {
    shutterName = rr.parentResourceName;
  }

  // Only add shutter name if it's not already in the resource name
  if (shutterName && resourceName?.includes(shutterName)) {
    return resourceName;
  }

  return [resourceName, shutterName].filter(Boolean).join(' / ');
}



// ---- Your checkDiff with an optional array filter ----
// Field values are RSF keys (your latest enum design)
type RSFKey = keyof RSF & keyof RSFAmendment;
